/**
 * VGrove 增强的 Loader 和 Action 创建器
 * 集成中间件系统的高级路由处理功能
 */

import { redirect, type LoaderFunctionArgs, type ActionFunctionArgs } from 'react-router';
import { middlewareManager, type MiddlewareFunction, type GuardFailureConfig } from './middleware';
import { debug } from './debug';
import type { AuthCheck } from './loaders';
import type { BaseUser } from './types';

/**
 * 增强的 Loader 配置
 */
export interface EnhancedLoaderConfig<TUser extends BaseUser = BaseUser> {
  /** 原始 loader 函数 */
  loader?: (args: LoaderFunctionArgs) => any;
  /** 中间件列表 */
  middlewares?: MiddlewareFunction<TUser>[];
  /** 认证检查函数 */
  authCheck?: AuthCheck<TUser>;
  /** 必需的角色 */
  requiredRoles?: string[];
  /** 必需的权限 */
  requiredPermissions?: string[];
  /** 守卫失败配置 */
  guardFailureConfig?: GuardFailureConfig;
  /** 是否跳过全局中间件 */
  skipGlobalMiddlewares?: boolean;
  /** 自定义错误处理 */
  errorHandler?: (error: any, args: LoaderFunctionArgs) => any;
  /** 数据转换器 */
  dataTransformer?: (data: any) => any;
  /** 缓存配置 */
  cache?: {
    enabled: boolean;
    ttl?: number; // 缓存时间（毫秒）
    key?: (args: LoaderFunctionArgs) => string;
  };
}

/**
 * 增强的 Action 配置
 */
export interface EnhancedActionConfig<TUser extends BaseUser = BaseUser> {
  /** 原始 action 函数 */
  action?: (args: ActionFunctionArgs) => any;
  /** 中间件列表 */
  middlewares?: MiddlewareFunction<TUser>[];
  /** 认证检查函数 */
  authCheck?: AuthCheck<TUser>;
  /** 必需的角色 */
  requiredRoles?: string[];
  /** 必需的权限 */
  requiredPermissions?: string[];
  /** 守卫失败配置 */
  guardFailureConfig?: GuardFailureConfig;
  /** 是否跳过全局中间件 */
  skipGlobalMiddlewares?: boolean;
  /** 自定义错误处理 */
  errorHandler?: (error: any, args: ActionFunctionArgs) => any;
  /** 数据验证器 */
  validator?: (formData: FormData) => Promise<any> | any;
  /** 成功重定向路径 */
  successRedirect?: string | ((result: any) => string);
  /** 失败重定向路径 */
  failureRedirect?: string | ((error: any) => string);
}

/**
 * 缓存管理器
 */
class CacheManager {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 60000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  // 定期清理过期缓存
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

const cacheManager = new CacheManager();

// 定期清理缓存
if (typeof window !== 'undefined') {
  setInterval(() => cacheManager.cleanup(), 60000); // 每分钟清理一次
}

/**
 * 创建增强的 Loader
 */
export function createEnhancedLoader<TUser extends BaseUser = BaseUser>(config: EnhancedLoaderConfig<TUser>) {
  return async (args: LoaderFunctionArgs) => {
    const startTime = Date.now();
    const url = new URL(args.request.url);

    debug.loader('开始执行增强 Loader', {
      pathname: url.pathname,
      hasOriginalLoader: !!config.loader,
      middlewareCount: config.middlewares?.length || 0,
      skipGlobalMiddlewares: config.skipGlobalMiddlewares
    });

    try {
      // 检查缓存
      if (config.cache?.enabled) {
        const cacheKey = config.cache.key
          ? config.cache.key(args)
          : `loader:${url.pathname}:${url.search}`;

        const cachedData = cacheManager.get(cacheKey);
        if (cachedData) {
          debug.loader('使用缓存数据', { cacheKey });
          return cachedData;
        }
      }

      let user: TUser | null = null;

      // 执行认证检查
      if (config.authCheck) {
        user = await config.authCheck();
        if (!user) {
          debug.loader('认证失败');
          throw new Response('Unauthorized', { status: 401 });
        }
      }

      // 执行全局中间件（如果未跳过）
      if (!config.skipGlobalMiddlewares) {
        const middlewareResult = await middlewareManager.execute(args, user);
        if (!middlewareResult.success) {
          debug.loader('全局中间件检查失败');
          if (middlewareResult.response) {
            throw middlewareResult.response;
          }
          throw new Response('Forbidden', { status: 403 });
        }

        // 更新用户信息（中间件可能会修改）
        if (middlewareResult.data?.user) {
          user = middlewareResult.data.user;
        }
      }

      // 执行自定义中间件
      if (config.middlewares && config.middlewares.length > 0) {
        for (const middleware of config.middlewares) {
          const context = {
            request: args.request,
            user,
            params: args.params,
            data: {},
            isLoader: true,
            isAction: false
          };

          const result = await middleware(context);
          if (!result.continue) {
            debug.loader('自定义中间件检查失败', { error: result.error });
            if (result.redirect) {
              throw result.redirect;
            }
            throw new Response(result.error || 'Forbidden', { status: 403 });
          }

          // 更新用户信息
          if (result.data?.user) {
            user = result.data.user;
          }
        }
      }

      // 执行角色检查
      if (config.requiredRoles && config.requiredRoles.length > 0) {
        if (!user) {
          debug.loader('角色检查失败：用户未认证');
          throw new Response('Unauthorized', { status: 401 });
        }

        const userRoles = user.roles || [];
        const hasRequiredRole = config.requiredRoles.some(role => userRoles.includes(role));

        if (!hasRequiredRole) {
          debug.loader('角色检查失败', {
            requiredRoles: config.requiredRoles,
            userRoles
          });
          throw new Response('Forbidden', { status: 403 });
        }
      }

      // 执行权限检查
      if (config.requiredPermissions && config.requiredPermissions.length > 0) {
        if (!user) {
          debug.loader('权限检查失败：用户未认证');
          throw new Response('Unauthorized', { status: 401 });
        }

        const userPermissions = user.permissions || [];
        const hasRequiredPermission = config.requiredPermissions.some(permission =>
          userPermissions.includes(permission)
        );

        if (!hasRequiredPermission) {
          debug.loader('权限检查失败', {
            requiredPermissions: config.requiredPermissions,
            userPermissions
          });
          throw new Response('Forbidden', { status: 403 });
        }
      }

      // 执行原始 loader
      let result = null;
      if (config.loader) {
        result = await config.loader(args);
      }

      // 数据转换
      if (config.dataTransformer && result) {
        result = config.dataTransformer(result);
      }

      // 添加用户信息到结果中
      if (user) {
        result = {
          ...result,
          user
        };
      }

      // 缓存结果
      if (config.cache?.enabled && result) {
        const cacheKey = config.cache.key
          ? config.cache.key(args)
          : `loader:${url.pathname}:${url.search}`;

        cacheManager.set(cacheKey, result, config.cache.ttl);
      }

      const duration = Date.now() - startTime;
      debug.loader('增强 Loader 执行完成', {
        pathname: url.pathname,
        duration: `${duration}ms`,
        hasResult: !!result
      });

      return result;

    } catch (error) {
      const duration = Date.now() - startTime;
      debug.loader('增强 Loader 执行失败', {
        pathname: url.pathname,
        duration: `${duration}ms`,
        error: error instanceof Error ? error.message : String(error)
      });

      // 自定义错误处理
      if (config.errorHandler) {
        return config.errorHandler(error, args);
      }

      throw error;
    }
  };
}

/**
 * 创建增强的 Action
 */
export function createEnhancedAction<TUser extends BaseUser = BaseUser>(config: EnhancedActionConfig<TUser>) {
  return async (args: ActionFunctionArgs) => {
    const startTime = Date.now();
    const url = new URL(args.request.url);

    debug.action('开始执行增强 Action', {
      pathname: url.pathname,
      method: args.request.method,
      hasOriginalAction: !!config.action,
      middlewareCount: config.middlewares?.length || 0,
      skipGlobalMiddlewares: config.skipGlobalMiddlewares
    });

    try {
      let user: TUser | null = null;

      // 执行认证检查
      if (config.authCheck) {
        user = await config.authCheck();
        if (!user) {
          debug.action('认证失败');

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Unauthorized'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Unauthorized', { status: 401 });
        }
      }

      // 执行全局中间件（如果未跳过）
      if (!config.skipGlobalMiddlewares) {
        const middlewareResult = await middlewareManager.execute(args, user);
        if (!middlewareResult.success) {
          debug.action('全局中间件检查失败');

          if (middlewareResult.response) {
            throw middlewareResult.response;
          }

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Forbidden'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Forbidden', { status: 403 });
        }

        // 更新用户信息
        if (middlewareResult.data?.user) {
          user = middlewareResult.data.user;
        }
      }

      // 执行自定义中间件
      if (config.middlewares && config.middlewares.length > 0) {
        for (const middleware of config.middlewares) {
          const context = {
            request: args.request,
            user,
            params: args.params,
            data: {},
            isLoader: false,
            isAction: true
          };

          const result = await middleware(context);
          if (!result.continue) {
            debug.action('自定义中间件检查失败', { error: result.error });

            if (result.redirect) {
              throw result.redirect;
            }

            if (config.failureRedirect) {
              const redirectPath = typeof config.failureRedirect === 'function'
                ? config.failureRedirect(new Error(result.error || 'Forbidden'))
                : config.failureRedirect;
              return redirect(redirectPath);
            }

            throw new Response(result.error || 'Forbidden', { status: 403 });
          }

          // 更新用户信息
          if (result.data?.user) {
            user = result.data.user;
          }
        }
      }

      // 执行角色检查
      if (config.requiredRoles && config.requiredRoles.length > 0) {
        if (!user) {
          debug.action('角色检查失败：用户未认证');

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Unauthorized'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Unauthorized', { status: 401 });
        }

        const userRoles = user.roles || [];
        const hasRequiredRole = config.requiredRoles.some(role => userRoles.includes(role));

        if (!hasRequiredRole) {
          debug.action('角色检查失败', {
            requiredRoles: config.requiredRoles,
            userRoles
          });

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Insufficient role'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Forbidden', { status: 403 });
        }
      }

      // 执行权限检查
      if (config.requiredPermissions && config.requiredPermissions.length > 0) {
        if (!user) {
          debug.action('权限检查失败：用户未认证');

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Unauthorized'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Unauthorized', { status: 401 });
        }

        const userPermissions = user.permissions || [];
        const hasRequiredPermission = config.requiredPermissions.some(permission =>
          userPermissions.includes(permission)
        );

        if (!hasRequiredPermission) {
          debug.action('权限检查失败', {
            requiredPermissions: config.requiredPermissions,
            userPermissions
          });

          if (config.failureRedirect) {
            const redirectPath = typeof config.failureRedirect === 'function'
              ? config.failureRedirect(new Error('Insufficient permission'))
              : config.failureRedirect;
            return redirect(redirectPath);
          }

          throw new Response('Forbidden', { status: 403 });
        }
      }

      // 数据验证
      let validatedData = null;
      if (config.validator) {
        const formData = await args.request.formData();
        validatedData = await config.validator(formData);
      }

      // 执行原始 action
      let result = null;
      if (config.action) {
        result = await config.action(args);
      }

      // 成功重定向
      if (config.successRedirect) {
        const redirectPath = typeof config.successRedirect === 'function'
          ? config.successRedirect(result)
          : config.successRedirect;
        return redirect(redirectPath);
      }

      const duration = Date.now() - startTime;
      debug.action('增强 Action 执行完成', {
        pathname: url.pathname,
        method: args.request.method,
        duration: `${duration}ms`,
        hasResult: !!result
      });

      return result || { success: true, data: validatedData };

    } catch (error) {
      const duration = Date.now() - startTime;
      debug.action('增强 Action 执行失败', {
        pathname: url.pathname,
        method: args.request.method,
        duration: `${duration}ms`,
        error: error instanceof Error ? error.message : String(error)
      });

      // 自定义错误处理
      if (config.errorHandler) {
        return config.errorHandler(error, args);
      }

      // 失败重定向
      if (config.failureRedirect) {
        const redirectPath = typeof config.failureRedirect === 'function'
          ? config.failureRedirect(error)
          : config.failureRedirect;
        return redirect(redirectPath);
      }

      throw error;
    }
  };
}

/**
 * 便捷函数：创建带认证的 Loader
 */
export function createAuthenticatedLoader<TUser extends BaseUser = BaseUser>(
  loader: (args: LoaderFunctionArgs) => any,
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedLoaderConfig<TUser>>
) {
  return createEnhancedLoader<TUser>({
    loader,
    authCheck,
    ...options
  });
}

/**
 * 便捷函数：创建带角色检查的 Loader
 */
export function createRoleBasedLoader<TUser extends BaseUser = BaseUser>(
  loader: (args: LoaderFunctionArgs) => any,
  requiredRoles: string[],
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedLoaderConfig<TUser>>
) {
  return createEnhancedLoader<TUser>({
    loader,
    authCheck,
    requiredRoles,
    ...options
  });
}

/**
 * 便捷函数：创建带权限检查的 Loader
 */
export function createPermissionBasedLoader<TUser extends BaseUser = BaseUser>(
  loader: (args: LoaderFunctionArgs) => any,
  requiredPermissions: string[],
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedLoaderConfig<TUser>>
) {
  return createEnhancedLoader<TUser>({
    loader,
    authCheck,
    requiredPermissions,
    ...options
  });
}

/**
 * 便捷函数：创建带认证的 Action
 */
export function createAuthenticatedAction<TUser extends BaseUser = BaseUser>(
  action: (args: ActionFunctionArgs) => any,
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedActionConfig<TUser>>
) {
  return createEnhancedAction<TUser>({
    action,
    authCheck,
    ...options
  });
}

/**
 * 便捷函数：创建带角色检查的 Action
 */
export function createRoleBasedAction<TUser extends BaseUser = BaseUser>(
  action: (args: ActionFunctionArgs) => any,
  requiredRoles: string[],
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedActionConfig<TUser>>
) {
  return createEnhancedAction<TUser>({
    action,
    authCheck,
    requiredRoles,
    ...options
  });
}

/**
 * 便捷函数：创建带权限检查的 Action
 */
export function createPermissionBasedAction<TUser extends BaseUser = BaseUser>(
  action: (args: ActionFunctionArgs) => any,
  requiredPermissions: string[],
  authCheck: AuthCheck<TUser>,
  options?: Partial<EnhancedActionConfig<TUser>>
) {
  return createEnhancedAction<TUser>({
    action,
    authCheck,
    requiredPermissions,
    ...options
  });
}

/**
 * 清除所有缓存
 */
export function clearAllCache(): void {
  cacheManager.clear();
}

/**
 * 获取缓存统计信息
 */
export function getCacheStats(): { size: number } {
  return {
    size: cacheManager['cache'].size
  };
}